import { createContext, useContext, useMemo, useState } from "react";

// Tracks the live height (px) of the announcement banner rendered above page
// content in AppShell.Main (currently the Link-Microsoft-account banner), or 0
// when none is shown.
//
// Pages that size themselves against the viewport (e.g. `vh - HEADER_HEIGHT - N`
// or `calc(100vh - 60px)`) live *below* that banner, so they must subtract its
// height too or they overflow the viewport by exactly the banner's height. The
// banner copy wraps to a variable number of lines across widths, so we measure
// it live rather than assume a fixed constant, and publish it here so every
// viewport-relative layout can react without prop drilling.
const LayoutOffsetContext = createContext({
  bannerOffset: 0,
  setBannerOffset: () => {},
});

export function LayoutOffsetProvider({ children }) {
  const [bannerOffset, setBannerOffset] = useState(0);
  const value = useMemo(
    () => ({ bannerOffset, setBannerOffset }),
    [bannerOffset]
  );
  return (
    <LayoutOffsetContext.Provider value={value}>
      {children}
    </LayoutOffsetContext.Provider>
  );
}

// Read hook: the extra vertical space (px) the announcement banner consumes.
// Subtract this from any viewport-relative height/footer calc in page content.
export function useBannerOffset() {
  return useContext(LayoutOffsetContext).bannerOffset;
}

// Setter hook for the banner to publish its measured height.
export function useSetBannerOffset() {
  return useContext(LayoutOffsetContext).setBannerOffset;
}
